import * as types from '../constants/cidraActions';
import { decideNextStep } from './decideNextStep';
import getErrorMessage from '../constants/getMessage';
import { CHANGE_PASSWORD } from '../constants/contexts';
import { cidMobile, AuthenticationProtocols } from '../constants/clientProtocols';
import { cidMobileErrorMap } from '../constants/cidMobileErrorMap';
import {
  startLoading, setServerState, doLogout, doLoginFailure,
  doSecondFactorFailure, setTempCodeToManual, doSetInitialTempCode
} from './shared';
import { doCidSessionFailure } from './startSession';

export function doVerifyPassword(inputPassword) {
  return function(dispatch, getState) {
    const {
      session, authClient, oneSignSessionId, skipSecondFactorToken, sessionService, workflow, cidMobileServiceInfo,
    } = getState().login;
    const { AuthVerifyPassword, AuthResponseAuthErrorContinued, } = AuthenticationProtocols;
    dispatch(doStartVerifyPassword() );
    dispatch(startLoading() );
    const password = session.encryptSecret(inputPassword);
    const verifyPassword = new AuthVerifyPassword(oneSignSessionId, password.value, null, skipSecondFactorToken);
    return authClient.sendRequest(verifyPassword).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode && serverState.responseType !== AuthResponseAuthErrorContinued) {
        // special case for confirmid mobile:
        // cannot handle expired password in this context
        if(cidMobileErrorMap.has(serverState.errorCode) && sessionService === cidMobile) {
          dispatch(doCidSessionFailure(serverState.errorCode, { errorCode: serverState.errorCode, }, workflow, cidMobileServiceInfo.optionalTransactionId) );
        }
        else {
          dispatch(doLogout() );
          dispatch(doLoginFailure(serverState.errorCode) );
        }
      }
      else {
        dispatch(doPasswordVerify(inputPassword) );
        dispatch(doSetInitialTempCode(serverState.temporaryCodeAllowed && serverState.temporaryCodeAvailable) );
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

export function doVerifyPin(inputPin) {
  return function(dispatch, getState) {
    const {
      session, authClient, oneSignSessionId,
    } = getState().login;
    const { AuthVerifyPin, } = AuthenticationProtocols;
    dispatch(doStartVerifyPin() );
    dispatch(startLoading() );
    const pin = session.encryptSecret(inputPin);
    const verifyPin = new AuthVerifyPin(oneSignSessionId, pin.value);
    return authClient.sendRequest(verifyPin).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode) {
        dispatch(doLogout() );
        dispatch(doSecondFactorFailure(serverState.errorCode) );
      }
      else {
        dispatch(doPinVerify() );
        dispatch(doSetInitialTempCode(serverState.temporaryCodeAllowed && serverState.temporaryCodeAvailable) );
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

export function doVerifyTempCode(inputTempCode) {
  return function(dispatch, getState) {
    const {
      session, authClient, oneSignSessionId,
    } = getState().login;
    const { AuthVerifyTempCode, } = AuthenticationProtocols;
    dispatch(doStartVerifyTempCode() );
    dispatch(startLoading() );
    const tempCode = session.encryptSecret(inputTempCode);
    const verifyTempCode = new AuthVerifyTempCode(oneSignSessionId, tempCode.value);
    return authClient.sendRequest(verifyTempCode).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode) {
        dispatch(doLogout() );
        dispatch(doSecondFactorFailure(serverState.errorCode) );
      }
      else {
        dispatch(doTempCodeVerify() );
        dispatch(setTempCodeToManual() );
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

export function doVerifySms(input) {
  return function(dispatch, getState) {
    const {
      session, authClient, oneSignSessionId,
    } = getState().login;
    const { AuthVerifyOutOfBand, } = AuthenticationProtocols;
    dispatch(doStartVerifySms() );
    dispatch(startLoading() );
    const encryptedToken = session.encryptSecret(input);
    const verifyOobToken = new AuthVerifyOutOfBand(oneSignSessionId, encryptedToken.value);
    return authClient.sendRequest(verifyOobToken).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode) {
        dispatch(doLogout() );
        dispatch(doSecondFactorFailure(serverState.errorCode) );
      }
      else {
        dispatch(doSmsVerify() );
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

export function doVerifyImprivataIdManual(input) {
  return function(dispatch, getState) {
    const {
      session, authClient, oneSignSessionId,
    } = getState().login;
    const { AuthVerifyImprivataId, } = AuthenticationProtocols;
    dispatch(doStartVerifyImprivataIdManual() );
    dispatch(startLoading() );
    const encryptedToken = session.encryptSecret(input);
    const verifyIidOtp = new AuthVerifyImprivataId(oneSignSessionId, encryptedToken.value);
    return authClient.sendRequest(verifyIidOtp).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode) {
        dispatch(doLogout() );
        dispatch(doSecondFactorFailure(serverState.errorCode) );
      }
      else {
        dispatch(doImprivataIdManualVerify() );
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

export function doVerifyOtp(input) {
  return function(dispatch, getState) {
    const {
      session, authClient, oneSignSessionId, sessionService, workflow, cidMobileServiceInfo,
    } = getState().login;
    const { AuthVerifyIdToken, } = AuthenticationProtocols;
    dispatch(doStartVerifyOtp() );
    dispatch(startLoading() );
    const encryptedToken = session.encryptSecret(input);
    const verifyIdToken = new AuthVerifyIdToken(oneSignSessionId, encryptedToken.value);
    return authClient.sendRequest(verifyIdToken).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode) {
        if(cidMobileErrorMap.has(serverState.errorCode) && sessionService === cidMobile){
          dispatch(doCidSessionFailure(serverState.errorCode, { errorCode: serverState.errorCode, }, workflow, cidMobileServiceInfo.optionalTransactionId) );
        }
        else {
          dispatch(doLogout() );
          dispatch(doSecondFactorFailure(serverState.errorCode) );
        }
      }
      else {
        dispatch(doOtpVerify() );
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

export function doVerifyEmail(input) {
  return function(dispatch, getState) {
    const {
      session, authClient, oneSignSessionId,
    } = getState().login;
    const { AuthVerifyEmail, } = AuthenticationProtocols;
    dispatch(doStartVerifyEmail() );
    dispatch(startLoading() );
    const encryptedEmailToken = session.encryptSecret(input);
    const emailToken = new AuthVerifyEmail(oneSignSessionId, encryptedEmailToken.value);
    return authClient.sendRequest(emailToken).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode) {
        dispatch(doLogout() );
        dispatch(doSecondFactorFailure(serverState.errorCode) );
      }
      else {
        dispatch(doEmailVerify() );
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

export function doVerifyIcpFace(icpVerificationCode, failure) {
  return function(dispatch, getState) {
    const { authClient, oneSignSessionId, } = getState().login;
    const { AuthIcpFace, AuthResponseAuthErrorContinued, } = AuthenticationProtocols;
    dispatch(doStartVerifyIcpFace() );
    dispatch(startLoading() );
    const authIcpFaceRequest = new AuthIcpFace(oneSignSessionId, icpVerificationCode);
    return authClient.sendRequest(authIcpFaceRequest).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode && serverState.responseType !== AuthResponseAuthErrorContinued) {
        dispatch(doLogout() );
        if (failure) {
          dispatch(failure);
        }
        else {
          dispatch(doSecondFactorFailure(serverState.errorCode) );
        }
      }
      else {
        dispatch(doIcpFaceVerify() );
        dispatch(doSetInitialTempCode(serverState.temporaryCodeAllowed && serverState.temporaryCodeAvailable) );
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

export function doResendEmail() {
  return function(dispatch, getState) {
    const { authClient, oneSignSessionId, } = getState().login;
    const { AuthInitiateEmailOtp, } = AuthenticationProtocols;
    dispatch(startLoading() );
    const initiateEmailOtp = new AuthInitiateEmailOtp(oneSignSessionId);
    return authClient.sendRequest(initiateEmailOtp).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode) {
        dispatch(doLogout() );
        dispatch(doSecondFactorFailure(serverState.errorCode) );
      }
      else {
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}


export function doVerifyChangePassword(newPassword) {
  return function(dispatch, getState) {
    const {
      session, authClient, oneSignSessionId,
    } = getState().login;
    const { AuthChangePassword, } = AuthenticationProtocols;
    dispatch(startLoading() );
    const encryptedPassword = session.encryptSecret(newPassword);
    const changePwd = new AuthChangePassword(oneSignSessionId, encryptedPassword.value);
    return authClient.sendRequest(changePwd).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode) {
        dispatch(doChangePasswordError(serverState.errorCode, CHANGE_PASSWORD) );
      }
      else {
        dispatch(doChangePasswordSuccess(newPassword) );
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

export function doInitiateImprivataIdPush() {
  return function(dispatch, getState) {
    const { authClient, oneSignSessionId, } = getState().login;
    const { AuthInitiateIdPush, AuthResponseAuthErrorContinued, } = AuthenticationProtocols;
    dispatch(startLoading() );
    const initiatePush = new AuthInitiateIdPush(oneSignSessionId);
    return authClient.sendRequest(initiatePush).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode &&
        serverState.responseType !== AuthResponseAuthErrorContinued) {
        dispatch(doLogout() );
        dispatch(doSecondFactorFailure(serverState.errorCode) );
      }
      else {
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

export function doResendSms() {
  return function(dispatch, getState) {
    const { authClient, oneSignSessionId, } = getState().login;
    const { AuthInitiateSms, } = AuthenticationProtocols;
    dispatch(startLoading() );
    const initiateSms = new AuthInitiateSms(oneSignSessionId);
    return authClient.sendRequest(initiateSms).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode) {
        dispatch(doLogout() );
        dispatch(doSecondFactorFailure(serverState.errorCode) );
      }
      else {
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

export function doInitiateIdv() {
  return function(dispatch, getState) {
    const { authClient, oneSignSessionId, } = getState().login;
    const { AuthInitiateIdv, } = AuthenticationProtocols;
    dispatch(startLoading() );
    const initiateIdv = new AuthInitiateIdv(oneSignSessionId);
    return authClient.sendRequest(initiateIdv).then(response => {
      const serverState = response.message;
      dispatch(setServerState(serverState) );
      if (serverState.errorCode) {
        dispatch(doLogout() );
        dispatch(doSecondFactorFailure(serverState.errorCode) );
      }
      else {
        dispatch(decideNextStep(serverState, false) );
      }
    });
  };
}

export function doStartVerifyPassword() {
  return { type: types.START_VERIFY_PASSWORD, };
}

export function doStartVerifyPin() {
  return { type: types.START_VERIFY_PIN, };
}

export function doPinVerify() {
  return { type: types.PIN_SUCCESS, };
}

export function doStartVerifyTempCode() {
  return { type: types.START_VERIFY_TEMPCODE, };
}

export function doTempCodeVerify() {
  return { type: types.TEMPCODE_SUCCESS, };
}

export function doStartVerifySms() {
  return { type: types.START_VERIFY_SMS, };
}

export function doSmsVerify() {
  return { type: types.SMS_SUCCESS, };
}

export function doStartVerifyImprivataIdManual() {
  return { type: types.START_VERIFY_IMPRIVATA_ID_MANUAL, };
}

export function doImprivataIdManualVerify() {
  return { type: types.IMPRIVATA_ID_MANUAL_SUCCESS, };
}

export function doStartVerifyOtp() {
  return { type: types.START_VERIFY_OTP, };
}

export function doOtpVerify() {
  return { type: types.OTP_SUCCESS, };
}

export function doStartVerifyEmail() {
  return { type: types.START_VERIFY_EMAIL, };
}

export function doEmailVerify() {
  return { type: types.EMAIL_SUCCESS, };
}

export function doChangePasswordError(errorCode, context) {
  const errorMessage = getErrorMessage(errorCode, context);
  return {
    errorCode,
    errorMessage,
    type: types.CHANGE_PASSWORD_RETRY,
  };
}

export function doChangePasswordSuccess(password) {
  return {
    type: types.CHANGE_PASSWORD_SUCCESS,
    password,
  };
}

export function doPasswordVerify(password) {
  return {
    password,
    type: types.PASSWORD_SUCCESS,
  };
}

export function doStartVerifyIcpFace() {
  return { type: types.START_VERIFY_ICP_FACE, };
}

export function doIcpFaceVerify() {
  return { type: types.ICP_FACE_SUCCESS, };
}

